import { map } from 'lodash'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import { Checkbox } from '@/components/ui'
import {
  OrderPaymentStatus,
  UserWithOrdersDto,
} from '../../../common/models/orders'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'

export interface OrderUsersTableProps {
  orders: UserWithOrdersDto[]
  sortBy?: string
  sortDirection?: 'DESC' | 'ASC'
  setSortDirection: (s?: 'DESC' | 'ASC') => void
  setSortBy: (sort?: string) => void
  handleSearchParams?: (col: IColumn, query: string | null) => void
  loading?: boolean
  noDataMessage?: string | JSX.Element
  hasEditPermission?: boolean
  maxHeight?: string
}

export const getStatusClass = (payment: OrderPaymentStatus) => {
  switch (payment) {
    case 'PAID':
    case 'REDEEMED':
    case 'PAYMENT_NOT_NEEDED':
      return 'text-monochrome-1'
    case 'FAILED':
    case 'CANCELED':
    case 'CHECKIN_FAILED':
    case 'INSTALLMENTS_FAILED':
      return 'text-accent-3'
    case 'REFUNDED':
      return 'text-cyan-500'
    case 'PAYMENT_PENDING':
    case 'INSTALLMENTS_ON_GOING':
      return 'text-accent-4'
  }
}

export const getIcon = (payment: OrderPaymentStatus) => {
  switch (payment) {
    case 'PAID':
    case 'REDEEMED':
    case 'PAYMENT_NOT_NEEDED':
      return (
        <Icon
          icon="check"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          className="text-accent-2"
          fill="none"
        />
      )
    case 'FAILED':
    case 'CANCELED':
    case 'CHECKIN_FAILED':
    case 'INSTALLMENTS_FAILED':
      return (
        <Icon
          icon="close-circle"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          className="text-accent-3"
          fill="none"
        />
      )
    case 'REFUNDED':
      return (
        <>
          <Icon
            icon="return"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            className="text-cyan-500"
            fill="none"
          />
        </>
      )
    case 'PAYMENT_PENDING':
    case 'INSTALLMENTS_ON_GOING':
      return (
        <Icon
          icon="clock"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          className="text-accent-4"
          fill="none"
        />
      )
  }
}

const getPaymentStatus = (status: string) => {
  const split = status.split('_')
  map(split, (str, index) => {
    split[index] = split[index][0] + split[index].substring(1).toLowerCase()
  })
  return split.join(' ')
}

export const OrderUsersTable = (props: OrderUsersTableProps): JSX.Element => {
  const navigate = useNavigate()
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const { festivalName, editionId } = useParams()

  const handleSelectAll = () => {
    if (!props.hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(props.orders.map((ord) => ord.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const { sortBy, setSortBy, sortDirection, setSortDirection } = props

  const handleClick = (id: string, checked: boolean) => {
    if (!props.hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
          disabled={!props.hasEditPermission}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
          disabled={!props.hasEditPermission}
        />
      ),

      width: '16px',
    },
    {
      title: 'Buyer',
      searchable: true,
      accessor: 'buyer',
      render: (row: UserWithOrdersDto) => (
        <div className="flex flex-col">
          <span>{row.buyer.name}</span>
          <span>{row.buyer.phoneNumber}</span>
          <span>{row.buyer.email}</span>
        </div>
      ),
    },
    {
      title: 'Phone Number',
      accessor: 'buyer.phoneNumber',
      render: (row: UserWithOrdersDto) => <span>{row.buyer.phoneNumber}</span>,
    },
    {
      title: 'Tag',
      accessor: 'tag',
      render: (row: UserWithOrdersDto) => (
        <div className="flex flex-col">
          <span>{row.vouchers.join(', ')}</span>
        </div>
      ),
    },
    {
      title: 'Referral Code',
      accessor: 'referralCode',
      render: (row: UserWithOrdersDto) => <span>{row.referralCode}</span>,
    },
    {
      title: 'Last Activity',
      accessor: 'lastActivityTime',
      sortable: true,
      render: (row) => (
        <span className="whitespace-nowrap">
          {formatDateTime(row.lastActivityTime, 'dd.MM.yyyy')}
        </span>
      ),
    },
    {
      title: 'View',
      accessor: '',
      render: (row) => (
        <button
          onClick={() => {
            navigate(`/clients/${festivalName}/${editionId}/${row.id}`)
          }}
          className="view text-accent-1 underline cursor-pointer"
          style={{ cursor: 'pointer' }}
        >
          View
        </button>
      ),
    },
  ]

  return (
    <Table
      columns={columns}
      data={props.orders}
      handleSortBy={handleSortBy}
      handleSearch={props.handleSearchParams}
      loading={props.loading}
      noDataMessage={props.noDataMessage}
      className="[&_th]:!whitespace-nowrap"
      maxHeight={props.maxHeight}
    />
  )
}
